import { useState, useEffect } from 'react';
import { 
  zafClient, 
  getCurrentUserName, 
  getCurrentTicketId,
  getRequesterData,
  popupError,
  type IZendeskCurrentUser,
  type RequesterData,
} from '../services/Zendesk';

interface ZendeskTicket {
  id: number;
  subject: string;
  status: string;
  requester_id: number;
  assignee_id?: number;
  brand_id?: number;
  custom_fields?: Array<{ id: number; value: unknown }>;
  created_at: string;
  updated_at: string;
}

interface ZendeskBrand {
  id: number;
  name: string;
  subdomain: string;
  url: string;
  has_help_center: boolean;
  help_center_state: string;
  active: boolean;
  default: boolean;
}

interface UseZendeskDataReturn {
  ticketId: number | null;
  ticket: ZendeskTicket | null;
  currentUser: IZendeskCurrentUser | null;
  requester: RequesterData | null;
  brand: ZendeskBrand | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useZendeskData(): UseZendeskDataReturn {
  const [ticketId, setTicketId] = useState<number | null>(null);
  const [ticket, setTicket] = useState<ZendeskTicket | null>(null);
  const [currentUser, setCurrentUser] = useState<IZendeskCurrentUser | null>(null);
  const [requester, setRequester] = useState<RequesterData | null>(null);
  const [brand, setBrand] = useState<ZendeskBrand | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchZendeskData = async () => {
    try {
      setIsLoading(true);
      setError(null);


      // Obter ID do ticket atual
      const currentTicketId = await getCurrentTicketId();
      setTicketId(currentTicketId);

      // Obter dados do usuário atual (agente)
      const user = await getCurrentUserName();
      setCurrentUser(user);

      // Obter dados completos do ticket
      const ticketResponse = await zafClient.request({
        url: `/api/v2/tickets/${currentTicketId}.json`,
        type: 'GET',
      }) as { ticket: ZendeskTicket };

      const ticketData = ticketResponse.ticket;
      setTicket(ticketData);

      // Obter dados do solicitante (requester) via ZAF + API REST
      const requesterData = await getRequesterData();
      if (requesterData) {
        setRequester(requesterData);
        console.log("✅ DEBUG: Requester carregado:", requesterData);
      } else if (ticketData.requester_id) {
        // Fallback: busca direta via API REST caso o ZAF não retorne
        console.warn("⚠️ getRequesterData retornou null, tentando fallback via API REST");
        const requesterResponse = await zafClient.request({
          url: `/api/v2/users/${ticketData.requester_id}.json`,
          type: 'GET',
        }) as { user: RequesterData };
        setRequester(requesterResponse.user);
      }

      // Obter dados do brand se disponível
      if (ticketData.brand_id) {
        try {
          const brandResponse = await zafClient.request({
            url: `/api/v2/brands/${ticketData.brand_id}.json`,
            type: 'GET',
          }) as { brand: ZendeskBrand };

          const brandData = brandResponse.brand;
          setBrand(brandData);
          console.log("🔧 DEBUG: Brand obtido do ticket:", brandData);
        } catch (brandError) {
          console.warn("⚠️ Não foi possível obter dados do brand:", brandError);
          setBrand(null);
        }
      } else {
        console.log("🔧 DEBUG: Ticket não possui brand_id");
        setBrand(null);
      }


    } catch (err) {
      console.error('❌ Erro ao carregar dados do Zendesk:', err);
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      popupError(`Erro ao carregar dados: ${errorMessage}`, false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchZendeskData();
  }, []);

  return {
    ticketId,
    ticket,
    currentUser,
    requester,
    brand,
    isLoading,
    error,
    refetch: fetchZendeskData,
  };
}

// Hook para obter valor de campo customizado específico
export function useTicketCustomField(fieldId: number) {
  const [value, setValue] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFieldValue = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const fieldKey = `ticket.customField:custom_field_${fieldId}`;
        const response = await zafClient.get(fieldKey);
        const fieldValue = response[fieldKey];
        
        setValue(fieldValue);

      } catch (err) {
        console.error(`❌ Erro ao obter campo ${fieldId}:`, err);
        const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFieldValue();
  }, [fieldId]);

  return { value, isLoading, error };
}
